import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/currencyUtils";
import { normalizeDecimalInput, parseDecimalSafe } from "@/lib/numberUtils";
import { useTranslation } from "react-i18next";
import { CreatableCombobox } from "@/components/ui/creatable-combobox";
import { HelpTip } from "@/components/ui/help-tip";

const DEFAULT_CATEGORIES = [
  "Proteins",
  "Vegetables",
  "Fruits",
  "Dairy",
  "Grains",
  "Spices",
  "Oils & Fats",
  "Condiments",
  "Beverages",
  "Sweeteners",
  "Other",
];

const UNITS = [
  "g",
  "kg",
  "mg",
  "ml",
  "L",
  "oz",
  "lb",
  "cup",
  "tbsp",
  "tsp",
  "piece",
  "unit"
];

const IngredientModal = ({ isOpen, onClose, ingredient, currency }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    category: "Proteins",
    bulk_price: "",
    bulk_quantity: "",
    unit: "g",
    notes: "",
    density_factor: "",  // Golden Thread: g/ml density
    unit_type: "weight"  // weight, volume, count
  });
  const [calculatedUnitCost, setCalculatedUnitCost] = useState("0.000000");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_CATEGORIES);

  // Load merged (defaults + custom) categories whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    axios
      .get(`${API}/ingredient-categories`)
      .then((res) => {
        if (cancelled) return;
        const all = res.data?.all;
        if (Array.isArray(all) && all.length > 0) setCategoryOptions(all);
      })
      .catch(() => {
        // fall back silently to defaults
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleCreateCategory = async (name) => {
    try {
      await axios.post(`${API}/ingredient-categories`, { name });
      setCategoryOptions((prev) =>
        prev.some((c) => c.toLowerCase() === name.toLowerCase()) ? prev : [...prev, name]
      );
      toast.success(`Category “${name}” added`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't create category";
      toast.error(msg);
      throw err;
    }
  };

  useEffect(() => {
    if (ingredient) {
      // Edit mode
      setFormData({
        name: ingredient.name,
        category: ingredient.category,
        bulk_price: ingredient.bulk_price,
        bulk_quantity: ingredient.bulk_quantity,
        unit: ingredient.unit,
        notes: ingredient.notes || "",
        density_factor: ingredient.density_factor || "",
        unit_type: ingredient.unit_type || "weight"
      });
      setCalculatedUnitCost(ingredient.unit_cost);
    } else {
      // Add mode - reset form
      setFormData({
        name: "",
        category: "Proteins",
        bulk_price: "",
        bulk_quantity: "",
        unit: "g",
        notes: ""
      });
      setCalculatedUnitCost("0.000000");
    }
    setErrors({});
  }, [ingredient, isOpen]);

  // Real-time unit cost calculation
  useEffect(() => {
    if (formData.bulk_price && formData.bulk_quantity) {
      try {
        const price = parseDecimalSafe(formData.bulk_price);
        const quantity = parseDecimalSafe(formData.bulk_quantity);
        
        if (price > 0 && quantity > 0) {
          const unitCost = price / quantity;
          setCalculatedUnitCost(unitCost.toFixed(6));
        } else {
          setCalculatedUnitCost("0.000000");
        }
      } catch (error) {
        setCalculatedUnitCost("0.000000");
      }
    } else {
      setCalculatedUnitCost("0.000000");
    }
  }, [formData.bulk_price, formData.bulk_quantity]);

  const handleInputChange = (field, value) => {
    // Normalize decimal inputs for ES/FR locales (comma → period)
    let normalizedValue = value;
    if (field === 'bulk_price' || field === 'bulk_quantity') {
      normalizedValue = normalizeDecimalInput(value);
    }
    
    setFormData(prev => ({ ...prev, [field]: normalizedValue }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Ingredient name is required";
    }

    if (!formData.bulk_price || isNaN(parseFloat(formData.bulk_price)) || parseFloat(formData.bulk_price) <= 0) {
      newErrors.bulk_price = "Valid bulk price is required";
    }

    if (!formData.bulk_quantity || isNaN(parseFloat(formData.bulk_quantity)) || parseFloat(formData.bulk_quantity) <= 0) {
      newErrors.bulk_quantity = "Valid bulk quantity is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    setLoading(true);

    try {
      // 'Other' intercept — if the user picked Other and typed a custom name,
      // promote that to a real (persisted) category before saving the ingredient.
      const payload = { ...formData };
      if (
        payload.category === "Other" &&
        (payload.other_category_name || "").trim()
      ) {
        const customName = payload.other_category_name.trim();
        try {
          await handleCreateCategory(customName);
        } catch {
          // toast surfaced inside handleCreateCategory; abort save.
          setLoading(false);
          return;
        }
        payload.category = customName;
      }
      delete payload.other_category_name;

      if (ingredient) {
        await axios.put(`${API}/ingredients/${ingredient.id}`, payload);
        toast.success(t("toasts.ingredientUpdated"));
      } else {
        await axios.post(`${API}/ingredients`, payload);
        toast.success(t("toasts.ingredientAdded"));
      }
      onClose(true);
    } catch (error) {
      console.error("Error saving ingredient:", error);
      toast.error(error.response?.data?.detail || t("toasts.ingredientFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white sm:max-w-[600px]" data-testid="ingredient-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl text-amber-500">
            {ingredient ? t("ingredientModal.editTitle") : t("ingredientModal.addTitle")}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {ingredient
              ? t("ingredientModal.editDesc")
              : t("ingredientModal.addDesc")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-slate-300">
              Ingredient Name *
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="e.g., Olive Oil, Saffron, Chicken Breast"
              className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${
                errors.name ? "border-red-500" : ""
              }`}
              data-testid="ingredient-name-input"
            />
            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
          </div>

          {/* Category and Unit */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category" className="text-slate-300">
                Category *
              </Label>
              <CreatableCombobox
                value={formData.category}
                options={categoryOptions}
                onChange={(val) => handleInputChange("category", val)}
                onCreate={handleCreateCategory}
                placeholder="Select a category"
                searchPlaceholder="Search or type to create…"
                emptyText="No matching category. Type to create one."
                createLabel="Create"
                testIdPrefix="category-combobox"
              />
              {formData.category === "Other" && (
                <div className="mt-2 space-y-1" data-testid="ingredient-other-intercept">
                  <Label htmlFor="ingredient-other-name" className="text-xs text-amber-400">
                    Specify custom category
                  </Label>
                  <Input
                    id="ingredient-other-name"
                    value={formData.other_category_name || ""}
                    onChange={(e) =>
                      handleInputChange("other_category_name", e.target.value)
                    }
                    placeholder="e.g. Bakery, Nori, Pickles…"
                    className="bg-slate-800 border-amber-500/40 text-white placeholder:text-slate-500"
                    data-testid="ingredient-other-input"
                  />
                  <p className="text-xs text-slate-500">
                    On save, we'll create this as a permanent category in your pantry.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit" className="text-slate-300">
                Unit *
              </Label>
              <Select value={formData.unit} onValueChange={(value) => handleInputChange("unit", value)}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {UNITS.map((unit) => (
                    <SelectItem key={unit} value={unit} className="text-white">
                      {unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bulk Price and Quantity */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bulk_price" className="text-slate-300">
                Bulk Price ({currency}) *
              </Label>
              <Input
                id="bulk_price"
                type="number"
                step="0.01"
                value={formData.bulk_price}
                onChange={(e) => handleInputChange("bulk_price", e.target.value)}
                placeholder="150.00"
                className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 font-mono ${
                  errors.bulk_price ? "border-red-500" : ""
                }`}
                data-testid="bulk-price-input"
              />
              {errors.bulk_price && <p className="text-red-500 text-sm">{errors.bulk_price}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk_quantity" className="text-slate-300">
                Bulk Quantity *
              </Label>
              <Input
                id="bulk_quantity"
                type="number"
                step="0.001"
                value={formData.bulk_quantity}
                onChange={(e) => handleInputChange("bulk_quantity", e.target.value)}
                placeholder="10"
                className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 font-mono ${
                  errors.bulk_quantity ? "border-red-500" : ""
                }`}
                data-testid="bulk-quantity-input"
              />
              {errors.bulk_quantity && <p className="text-red-500 text-sm">{errors.bulk_quantity}</p>}
            </div>
          </div>

          {/* Calculated Unit Cost Display */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-400 mb-1">Calculated Unit Cost</div>
                <div className="text-2xl font-bold text-amber-500 font-mono" data-testid="calculated-unit-cost">
                  {formatCurrency(calculatedUnitCost, currency, 6)}
                  <span className="text-lg text-slate-400 ml-2">/ {formData.unit}</span>
                </div>
              </div>
              <div className="text-xs text-slate-500 bg-slate-800/50 px-3 py-2 rounded">
                6-decimal precision
              </div>
            </div>
          </div>

          {/* 🍯 Golden Thread: Density Factor */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🍯</span>
              <div>
                <div className="text-sm font-semibold text-amber-500">Golden Thread - Density</div>
                <div className="text-xs text-slate-400">For volume ↔ weight conversion (e.g., ml to kg)</div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="density_factor" className="text-slate-300 flex items-center">
                  Density Factor (g/ml)
                  <HelpTip title="Density factor (g/ml)" testId="help-density-factor">
                    Lets you cost an ingredient that's <em>purchased by weight</em>{" "}
                    (e.g. honey by the kg) but <em>used by volume</em> in recipes
                    (e.g. 20&nbsp;ml in a glaze). Density bridges the two so the
                    cost stays accurate.
                    <br />
                    <span className="text-xs text-slate-500 mt-2 block">
                      Common: water 1.0 · honey 1.42 · olive oil 0.92 · maple syrup 1.33
                    </span>
                  </HelpTip>
                </Label>
                <Input
                  id="density_factor"
                  type="number"
                  step="0.000001"
                  value={formData.density_factor}
                  onChange={(e) => handleInputChange("density_factor", e.target.value)}
                  placeholder="1.420000 (e.g., Honey)"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 font-mono"
                  data-testid="density-factor-input"
                />
                <p className="text-xs text-slate-500">
                  Examples: Honey 1.42, Olive Oil 0.92, Water 1.0
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="unit_type" className="text-slate-300">
                  Unit Type
                </Label>
                <Select value={formData.unit_type} onValueChange={(value) => handleInputChange("unit_type", value)}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="unit-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="weight" className="text-white">Weight (kg, g)</SelectItem>
                    <SelectItem value="volume" className="text-white">Volume (ml, L)</SelectItem>
                    <SelectItem value="count" className="text-white">Count (pieces)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {formData.unit_type === "volume" ? "Enable ml/L ↔ kg conversion" : "Standard weight-based pricing"}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-slate-300">
              Notes (Optional)
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="Additional notes about supplier, quality, etc."
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 min-h-[80px]"
              data-testid="notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onClose(false)}
            className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
            disabled={loading}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
            disabled={loading}
            data-testid="save-ingredient-button"
          >
            {loading ? t("ingredientModal.saving") : ingredient ? t("ingredientModal.updateIngredient") : t("ingredientModal.addIngredient")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IngredientModal;
